import { useState } from 'react'
import { Trophy as TrophyIcon, Plus, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import { tierStyle } from './trophyShared'
import type { OriginalTrophy } from '@/api/trophies'

export default function TrophyDetailDialog({
  trophy,
  onClose,
  onAddToShelf,
  onDelete,
}: {
  trophy: OriginalTrophy | null
  onClose: () => void
  onAddToShelf?: (t: OriginalTrophy) => void
  onDelete?: (t: OriginalTrophy) => void | Promise<unknown>
}) {
  const t = trophy
  const { color, glow } = t ? tierStyle(t.rarity) : { color: '#999', glow: 'transparent' }
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!t || !onDelete) return
    setDeleting(true)
    try {
      await onDelete(t)
      setConfirmOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={!!t} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        {t && (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">トロフィー詳細</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3 pt-1">
              <div
                className="flex h-20 w-20 items-center justify-center rounded-full"
                style={{ background: `${color}1a` }}
              >
                <TrophyIcon size={40} style={{ color, filter: `drop-shadow(0 0 8px ${glow})` }} />
              </div>

              <div className="text-center">
                <p className="text-lg font-semibold">{t.name}</p>
                {t.title && <p className="text-sm text-muted-foreground">{t.title}</p>}
                <p className="mt-1 text-sm font-medium" style={{ color }}>
                  レア度 {t.rarity} / 10
                </p>
              </div>

              {onAddToShelf && t.position === null && (
                <Button size="sm" className="gap-1.5" onClick={() => onAddToShelf(t)}>
                  <Plus size={16} />
                  棚に追加
                </Button>
              )}

              {t.skill && (
                <div className="w-full">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">スキル</p>
                  <p className="rounded-md bg-muted p-2.5 text-sm">{t.skill}</p>
                </div>
              )}

              {t.self_pr && (
                <div className="w-full">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">自己PR</p>
                  <p className="whitespace-pre-wrap rounded-md bg-muted p-2.5 text-sm leading-relaxed">
                    {t.self_pr}
                  </p>
                </div>
              )}

              {t.material.length > 0 && (
                <div className="w-full">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">材料</p>
                  <div className="space-y-1">
                    {t.material.map((m) => (
                      <div key={`${m.kind}-${m.id}`} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm">
                        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {m.kind === 'achievement' ? '実績' : 'トロフィー'}
                        </span>
                        <span className="truncate">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {t.supplement && (
                <div className="w-full">
                  <p className="mb-1 text-xs font-medium text-muted-foreground">補足</p>
                  <p className="whitespace-pre-wrap rounded-md bg-muted p-2.5 text-sm">{t.supplement}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {new Date(t.created_at).toLocaleDateString('ja-JP')} 生成
              </p>

              {onDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  className="flex items-center gap-1 text-xs text-destructive hover:underline"
                >
                  <Trash2 size={13} />
                  このトロフィーを削除
                </button>
              )}
            </div>

            <ConfirmDialog
              open={confirmOpen}
              onOpenChange={setConfirmOpen}
              title="トロフィーを削除しますか？"
              description="この操作は元に戻せません。"
              confirmLabel="削除する"
              variant="destructive"
              isPending={deleting}
              onConfirm={handleDelete}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
